import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { getEnumValue } from 'lib/util/enum';
import { classnames, TArg, TTailwindString } from 'tailwindcss-classnames';
import { ValidHeadingLevelOptions, ValidHeadingLevels } from './_types';
import { isValidHeadingLevel } from './type-guards';
import { logWarning } from 'lib/util/errors';

export type { ValidHeadingLevels };

/**
 * Set a valid heading level.
 * @param {string} string headingLevel rendering parameter
 * @param {number} offset offset for the child heading
 * @returns {string} headingLevel
 */
export default function SetHeadingLevel(string: string, offset = 0): string {
  let headingIndex = isValidHeadingLevel(string) ? ValidHeadingLevelOptions.indexOf(string) : -1;
  // Default to h2 if invalid index is added
  if (headingIndex === -1) {
    if (process.env.NODE_ENV === 'development') {
      logWarning(
        `Heading level '${string}' not found. Its likely that ValidHeadingLevels and the options in SetHeadingLevel dont match.`
      );
    }
    headingIndex = 0;
  }
  // Ensure our offset is zero or a positive integer
  const cleanOffset = offset < 0 ? 0 : Math.floor(offset);
  headingIndex += cleanOffset;
  // If we've used all our available headers, return p
  if (headingIndex >= ValidHeadingLevelOptions.length) {
    return 'p';
  }
  return ValidHeadingLevelOptions[headingIndex];
}

/**
 * Ensure any heading size field used in a sitecore rendering is valid.
 * If an unvalid size is used, default to a valid heading size - l.
 */
export type ValidHeadingSizes = 'xl' | 'l' | 'm' | 's' | 'xs' | 'xxs';

const headingSizes: Record<ValidHeadingSizes, TTailwindString> = {
  xl: classnames('heading-xl'),
  l: classnames('heading-lg'),
  m: classnames('heading-md'),
  s: classnames('heading-sm'),
  xs: classnames('heading-xs'),
  xxs: classnames('heading-2xs'),
};

/**
 * Set a valid heading size.
 * @param {string} string headingSize string, from a parameter or field value.
 * @returns {string: TArg} valid tailwind heading size utility.
 */
export function setHeadingSize(size: string | undefined): TArg | undefined {
  if (typeof size === 'undefined' || !headingSizes.hasOwnProperty(size)) {
    return headingSizes['l'];
  }

  return headingSizes[size as ValidHeadingSizes];
}
export function setHeadingAlign(size: string | undefined): TArg | undefined {
  if (typeof size === 'undefined') {
    return headingSizes['l'];
  }

  return headingSizes[size as ValidHeadingSizes];
}

export type TextAlignOptions = 'left' | 'center' | 'right';
export const getHeadingAlign = (item: string | undefined) =>
  classnames({
    'text-left': item === 'left',
    'text-center': item === 'center',
    'text-right': item === 'right',
  });

export const getTextAlignFromEnum = (item: Sitecore.Override.ItemEx | undefined) => {
  const textAlignValue = getEnumValue(item);
  return classnames({
    'text-left': textAlignValue === 'left',
    'text-center': textAlignValue === 'center',
    'text-right': textAlignValue === 'right',
  });
};

export type VideoSides = 'left' | 'right';
